import { isAnyOf, type Middleware } from "@reduxjs/toolkit";

import aiChatApi from "@/providers/store/api/ai-chat/ai-chat";
import aiGlossaryApi from "@/providers/store/api/ai-glossary/ai-glossary";
import districtMembersApi from "@/providers/store/api/district-members/district-members";
import driveApi from "@/providers/store/api/drive/drive";
import edlinkApi from "@/providers/store/api/edlink/edlink";
import findMySchoolApi from "@/providers/store/api/find-my-school/find-my-school";
import platformDistrictsApi from "@/providers/store/api/platform-districts/platform-districts";
import platformMembersApi from "@/providers/store/api/platform-members/platform-members";
import supportApi from "@/providers/store/api/support/support";

import { broadcastsApi } from "./api/admin/broadcasts";
import { chatAlertsApi } from "./api/admin/chat-alerts";
import { adminChatRoomsApi } from "./api/admin/chat-rooms";
import { adminUsersApi } from "./api/admin/users";
import authApi from "./api/auth/auth";
import customLayersApi from "./api/custom-layers/custom-layers";
import districtsApi from "./api/districts/districts";
import documentsApi from "./api/documents/documents";
import geocodingApi from "./api/geocoding/geocoding";
import importsApi from "./api/imports/imports";
import mapLevelsApi from "./api/map-levels/map-levels";
import mapSettingPresetsApi from "./api/map-setting-presets/map-setting-presets";
import projectsApi from "./api/projects/projects";
import reportsApi from "./api/reports/reports";
import sampleDataApi from "./api/sample-data/sample-data";
import schoolsApi from "./api/schools/schools";
import shapesApi from "./api/shapes/shapes";
import storageApi from "./api/storage/storage";
import studentsApi from "./api/students/students";
import usersApi from "./api/users/users";
import { logout } from "./slices/user/slice";

import type userReducer from "./slices/user/slice";

/**
 * APIs whose cache holds per-session data. authApi is left out: it has only mutations,
 * and resetting it would clear the sign-in mutation state the sign-in page redirects on.
 */
export const SESSION_DATA_APIS = [
  customLayersApi,
  mapLevelsApi,
  usersApi,
  districtsApi,
  schoolsApi,
  sampleDataApi,
  projectsApi,
  studentsApi,
  documentsApi,
  storageApi,
  geocodingApi,
  importsApi,
  reportsApi,
  districtMembersApi,
  edlinkApi,
  platformMembersApi,
  platformDistrictsApi,
  aiChatApi,
  aiGlossaryApi,
  chatAlertsApi,
  adminUsersApi,
  adminChatRoomsApi,
  broadcastsApi,
  shapesApi,
  driveApi,
  mapSettingPresetsApi,
  findMySchoolApi,
  supportApi,
] as const;

const isLoginSuccess = isAnyOf(
  authApi.endpoints.signIn.matchFulfilled,
  authApi.endpoints.signUp.matchFulfilled,
  authApi.endpoints.googleCallback.matchFulfilled,
  authApi.endpoints.googleSignUpCallback.matchFulfilled,
  authApi.endpoints.microsoftCallback.matchFulfilled,
  authApi.endpoints.microsoftSignUpCallback.matchFulfilled,
);

/**
 * Drops every cached API response when a session ends or a new one starts, so a fresh
 * login never renders data (or skips side effects like district selection) from the
 * previous session. The store survives soft navigation between sign-in and the panel.
 */
export const sessionResetMiddleware: Middleware<object, { user: ReturnType<typeof userReducer> }> = (store) => (next) => (action) => {
  // Only the logout that ends an active session resets. Queries still mounted after it
  // refetch without a token and each 401 dispatches logout again; resetting on those
  // would loop until the redirect to sign-in unmounts them.
  const isSessionEnd = logout.match(action) && Boolean(store.getState().user.profile);

  const result = next(action);

  if (isSessionEnd || isLoginSuccess(action)) {
    SESSION_DATA_APIS.forEach((api) => store.dispatch(api.util.resetApiState()));
  }

  return result;
};
